import type {
  CreateIntentDto,
  CreateProposalDto,
  SegmentProposal,
  SegmentResult,
  SparkIntent,
  SparkIntentListItem,
  SparkPrioritizedIntentResults,
  SparkSegment,
  SparkWeekPreview,
  UpdateIntentAvailabilityByDateDto,
  UpdateIntentAvailabilityHorizonDto,
} from '@/core/data/spark/spark.interface';
import type { SparkRepository } from '@/core/data/spark/spark.repository';
import type { IntentResultOptions, RespondSparkInvitationDto } from '@/core/network/api/spark/spark.api';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';

export class SparkService {
  private readonly repository: SparkRepository;

  constructor(repository: SparkRepository) {
    this.repository = repository;
  }

  async createIntent(payload: CreateIntentDto): Promise<Result<SparkIntent>> {
    try {
      const response = await this.repository.createIntent(payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateIntent(intentId: string, payload: CreateIntentDto): Promise<Result<SparkIntent>> {
    try {
      const response = await this.repository.updateIntent(intentId, payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async markProposalSuccessAsRead(proposalId: string): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.markProposalSuccessAsRead(proposalId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateIntentAvailabilityByDate(
    dateString: string,
    payload: UpdateIntentAvailabilityByDateDto,
  ): Promise<Result<SparkIntent>> {
    try {
      const response = await this.repository.updateIntentAvailabilityByDate(dateString, payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateIntentAvailabilityHorizon(payload: UpdateIntentAvailabilityHorizonDto): Promise<Result<SparkIntent[]>> {
    try {
      const response = await this.repository.updateIntentAvailabilityHorizon(payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async initializeAvailabilityHorizon(cityId: string): Promise<Result<SparkIntent[]>> {
    try {
      const response = await this.repository.initializeAvailabilityHorizon(cityId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteIntent(intentId: string): Promise<Result<any>> {
    try {
      const response = await this.repository.deleteIntent(intentId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createProposal(payload: CreateProposalDto): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.createProposal(payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createInvitations(proposalId: string, userIds: string[]): Promise<Result<any>> {
    try {
      const response = await this.repository.createProposalInvitations(proposalId, userIds);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async acceptInvitation(proposalId: string, chessSetCount?: number): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.acceptProposalInvitation(proposalId, chessSetCount);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async declineInvitation(proposalId: string): Promise<Result<any>> {
    try {
      const response = await this.repository.declineProposalInvitation(proposalId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async withdrawInvitation(proposalId: string): Promise<Result<any>> {
    try {
      const response = await this.repository.withdrawProposalInvitation(proposalId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getRespondSparkInvitations(): Promise<Result<RespondSparkInvitationDto[]>> {
    try {
      const response = await this.repository.getRespondSparkInvitations();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getProposal(id: string): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.getProposal(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createConfirmation(proposalId: string, intentId: string): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.createConfirmation(proposalId, intentId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteConfirmation(proposalId: string): Promise<Result<SegmentProposal | null>> {
    try {
      const response = await this.repository.deleteConfirmation(proposalId);
      return { data: response?.data ?? null };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateConfirmationChessSetCount(proposalId: string, chessSetCount: number): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.updateConfirmationChessSetCount(proposalId, chessSetCount);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async meetAnyways(proposalId: string): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.meetAnyways(proposalId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async undoMeetAnyways(proposalId: string): Promise<Result<SegmentProposal>> {
    try {
      const response = await this.repository.undoMeetAnyways(proposalId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getIntent(id: string): Promise<Result<SparkIntent>> {
    try {
      const response = await this.repository.getIntent(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getIntentByDate(dateString: string): Promise<Result<SparkIntent>> {
    try {
      const response = await this.repository.getIntentByDate(dateString);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getWeekPreview(from: string): Promise<Result<SparkWeekPreview>> {
    try {
      const response = await this.repository.getWeekPreview(from);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getIntents(): Promise<Result<SparkIntentListItem[]>> {
    try {
      const response = await this.repository.getIntents();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async extendAvailabilities(cityId: string): Promise<Result<SparkIntent[]>> {
    try {
      const response = await this.repository.extendAvailabilities(cityId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async findMatches(locationIds: string[], start: Date, end: Date): Promise<Result<SparkSegment[]>> {
    try {
      const response = await this.repository.findMatches(locationIds, start, end);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async findIntentResults(intentId: string, options?: IntentResultOptions): Promise<Result<SegmentResult[]>> {
    try {
      const response = await this.repository.findIntentResults(intentId, options);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async findPrioritizedIntentResults(
    intentId: string,
    gpsCoords?: { lat: number; lng: number },
  ): Promise<Result<SparkPrioritizedIntentResults>> {
    try {
      const response = await this.repository.findPrioritizedIntentResults(intentId, gpsCoords);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createFirestoreProposalDoc(id: string): Promise<Result<any>> {
    try {
      const response = await this.repository.createFirestoreProposalDoc(id);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createFirestoreIntentDoc(id: string): Promise<Result<any>> {
    try {
      const response = await this.repository.createFirestoreIntentDoc(id);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }
}
